/**
 * @fileoverview Hook for updating project core metadata with optimistic updates.
 *
 * Provides a simple interface to update any project field while handling
 * persistence, optimistic updates, and rollback on failure.
 */

'use client';

import { useCallback } from 'react';
import { useToast } from '@/hooks/use-toast';
import { Project } from '@/domains/projects/schemas';
import { updateProjectDocument } from '../services/update-project';

/**
 * A hook for managing updates to a single project's core metadata fields
 * (title, blurb, active entity pointers, etc.), encapsulating optimistic
 * updates and persistence logic.
 *
 * @param project The full project object.
 * @param setProject The state setter for the project from the parent context.
 * @returns An object containing the `updateProjectCore` function.
 */
export function useProjectCore(
  project: Project,
  setProject: React.Dispatch<React.SetStateAction<Project>>,
) {
  const { toast } = useToast();

  const updateProjectCore = useCallback(async (updates: Partial<Project>) => {
    // Capture current state for potential rollback
    let previousProject: Project | null = null;

    // Use functional update to avoid stale closure race conditions
    setProject(prev => {
      previousProject = prev;
      return {
        ...prev,
        ...updates,
        updatedAt: new Date().toISOString(),
      };
    });

    try {
      await updateProjectDocument(project.id, updates);
    } catch (error) {
      console.error("Failed to update project document, rolling back:", error);
      if (previousProject) {
        setProject(previousProject);
      }
      toast({ variant: 'destructive', title: 'Save Failed', description: 'Could not save project metadata to the database.' });
      throw error; // Re-throw to let the caller handle UI state if needed
    }
  }, [project.id, setProject, toast]);

  return {
    updateProjectCore,
  };
}
